import type { Extensions } from '@tiptap/core';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCaret from '@tiptap/extension-collaboration-caret';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import TextAlign from '@tiptap/extension-text-align';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import StarterKit from '@tiptap/starter-kit';
import type * as Y from 'yjs';
import type { Awareness } from 'y-protocols/awareness';

export type CaretUser = { name: string; color: string };

/**
 * The board's undo manager as y-tiptap sees it: the same stacks and methods, but closing an editor never destroys it
 * (y-tiptap's undo plugin destroys the manager it is given on unmount) and the listeners it added are removed. Tiptap's
 * `restore` hack (it re-attaches a destroyed manager on the next mount) is disabled: re-attaching a live manager would
 * record every transaction twice.
 */
export function sharedUndoManager(um: Y.UndoManager): Y.UndoManager {
  const added: [string, (...args: never[]) => void][] = [];
  return new Proxy(um, {
    set(target, key, value) {
      return key === 'restore' ? true : Reflect.set(target, key, value);
    },
    get(target, key) {
      if (key === 'restore') return undefined;
      if (key === 'destroy') return () => added.splice(0).forEach(([ev, fn]) => target.off(ev as 'stack-item-added', fn as never));
      if (key === 'on')
        return (ev: string, fn: (...args: never[]) => void) => {
          added.push([ev, fn]);
          target.on(ev as 'stack-item-added', fn as never);
        };
      const v = Reflect.get(target, key, target) as unknown;
      return typeof v === 'function' ? (v as (...a: unknown[]) => unknown).bind(target) : v;
    },
  });
}

/**
 * Tiptap extensions of an object's text: StarterKit without its own history nor trailing node (opening an editor must
 * never write), alignment, colors, task lists, collaboration.
 */
export function richExtensions(o: { fragment: Y.XmlFragment; undoManager: Y.UndoManager; awareness?: Awareness; user?: CaretUser }): Extensions {
  const exts: Extensions = [
    StarterKit.configure({ undoRedo: false, trailingNode: false, link: { openOnClick: false, autolink: true, protocols: ['mailto'] } }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    TextStyle,
    Color,
    TaskList,
    TaskItem.configure({ nested: true }),
    Collaboration.configure({ fragment: o.fragment, yUndoOptions: { undoManager: sharedUndoManager(o.undoManager) } }),
  ];
  if (o.awareness && o.user) exts.push(CollaborationCaret.configure({ provider: { awareness: o.awareness }, user: o.user }));
  return exts;
}
